import React, { useEffect, useState } from "react";
import { Button } from "antd";
import styled from "styled-components";
import moment from "moment";
import { saveAs } from "file-saver";
import JSZip from "jszip";

import { PRIMARY_600, PRIMARY_800, PRIMARY_400, DEEP_SLATE } from "../../constant/colors";
import { useTranslation } from "react-i18next";
import { ReactComponent as ExportIcon } from "../../assets/svg/ExportIcon.svg";
import { ReactComponent as LoadingIcon } from "../../assets/svg/loading.svg";
import { parseFilterValues } from "../../constant/parse";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEnrollmentClaimsReportData,
  selectEnrollmentsReportData,
  selectClaimsReportData,
  selectOrdersReportData,
  selectCompletedOrdersReportData,
  selectReportColumns
} from "../../store/selector";
import { REPORT_NAME_DATE_FORMAT } from "../../constant/date";
import {
  CLAIMS_TYPE,
  ENROLLMENTS_TYPE,
  ENROLLMENT_CLAIMS_TYPE,
  ORDERS_TYPE,
  COMPLETED_ORDERS_TYPE,
  ColumnsKeys
} from "../../constant/filters";
import ACTIONS from "../../store/action";
import SuccessMessage from "../customComponent/customMessages/SuccessMessage";

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${PRIMARY_600}!important;
  color: white !important;
  border-radius: 4px;
  height: 36px;
  width: 180px;
  border-color: ${PRIMARY_600}!important;
  padding: 0px 20px 0px 20px;
  :hover {
    color: white !important;
    background: ${PRIMARY_800}!important;
    border-color: ${PRIMARY_800}!important;
  }
  :active {
    color: white !important;
    background: ${PRIMARY_600}!important;
    border: 4px solid ${PRIMARY_400}!important;
  }

  :disabled {
    color: white;
    background: ${PRIMARY_600} !important;
    border: 1px solid ${PRIMARY_600} !important;
    box-shadow: none;
    opacity: 0.3 !important;
    box-shadow: 0px 1px 2px 0px ${DEEP_SLATE};
    :hover {
      color: white;
      opacity: 0.3;
      background: ${PRIMARY_600} !important;
      border: 1px solid ${PRIMARY_600};
      cursor: not-allowed !important;
    }
  }
  ${({ isLoading }) =>
    isLoading &&
    `pointer-events: none !important;
  `}
`;

const CustomLoadingIcon = styled(LoadingIcon)`
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  animation: spin 1s linear infinite;
`;

const ExportButton = ({ isExportEnabled, selectedDataTypes, filters, isLoading }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isFetchingFinished, setIsFetchingFinished] = useState(false);
  const enrollmentsReportData = useSelector(selectEnrollmentsReportData);
  const enrollmentClaimsReportData = useSelector(selectEnrollmentClaimsReportData);
  const claimsReportData = useSelector(selectClaimsReportData);
  const ordersReportData = useSelector(selectOrdersReportData);
  const completedOrdersReportData = useSelector(selectCompletedOrdersReportData);
  const reportColumns = useSelector(selectReportColumns);
  const buttonLoadingIcon = <CustomLoadingIcon />;

  const getSelectedColumns = (reportType) => {
    const columns = reportColumns?.[reportType];
    if (!columns) return [];
    return Object.values(columns)
      .filter((col) => col.display)
      .map((col) => col.dataIndex);
  };

  const getEnrollmentsReportDataAct = (filters) => dispatch(ACTIONS.getEnrollmentsReportData(filters));
  const getEnrollmentClaimsReportDataAct = (filters) => dispatch(ACTIONS.getEnrollmentClaimsReportData(filters));
  const getClaimsReportDataAct = (filters) => dispatch(ACTIONS.getClaimsReportData(filters));
  const getOrdersReportDataAct = (filters) => dispatch(ACTIONS.getOrdersReportData(filters));
  const getCompletedOrdersReportDataAct = (filters) => dispatch(ACTIONS.getCompletedOrdersReportData(filters));
  const setEnrollmentsReportDataAct = (enrollmentsReportData) =>
    dispatch(ACTIONS.setEnrollmentsReportData(enrollmentsReportData));
  const setEnrollmentClaimsReportDataAct = (enrollmentClaimsReportData) =>
    dispatch(ACTIONS.setEnrollmentClaimsReportData(enrollmentClaimsReportData));
  const setClaimsReportDataAct = (claimsReportData) => dispatch(ACTIONS.setClaimsReportData(claimsReportData));
  const setOrdersReportDataAct = (ordersReportData) => dispatch(ACTIONS.setOrdersReportData(ordersReportData));
  const setCompletedOrdersReportDataAct = (completedOrdersReportData) =>
    dispatch(ACTIONS.setCompletedOrdersReportData(completedOrdersReportData));

  const selectedTypes = Object.keys(Object.assign({}, ...selectedDataTypes.filter((type) => Object.values(type)[0])));
  const selectedDataTypesCount = selectedTypes.length;

  const handleReportExport = async () => {
    setIsFetchingFinished(false);
    if (selectedTypes.includes(ENROLLMENTS_TYPE)) await fetchApplications();
    if (selectedTypes.includes(CLAIMS_TYPE)) await fetchClaims();
    if (selectedTypes.includes(ENROLLMENT_CLAIMS_TYPE)) await fetchEnrollmentClaims();
    if (selectedTypes.includes(ORDERS_TYPE)) await fetchOrders();
    if (selectedTypes.includes(COMPLETED_ORDERS_TYPE)) await fetchCompletedOrders();
    setIsFetchingFinished(true);
  };

  useEffect(() => {
    if (selectedDataTypesCount === 1) {
      let type;
      if (enrollmentsReportData) {
        type = "Enrollments";
      } else if (enrollmentClaimsReportData) {
        type = "EnrollmentsWithClaims";
      } else if (claimsReportData) {
        type = "Claims";
      } else if (ordersReportData) {
        type = "Orders";
      } else if (completedOrdersReportData) {
        type = "CompletedOrders";
      }
      const dateRangeFilter = enrollmentsReportData
        ? filters.timeRangeFilter
        : enrollmentClaimsReportData
        ? filters.enrollmentClaimsTimeRangeFilter
        : claimsReportData
        ? filters.claimTimeRangeFilter
        : ordersReportData
        ? filters.orderTimeRangeFilter
        : filters.completedOrderTimeRangeFilter;
      const data =
        enrollmentsReportData ||
        enrollmentClaimsReportData ||
        claimsReportData ||
        ordersReportData ||
        completedOrdersReportData;
      data && downloadFile(data, dateRangeFilter, type);
    } else {
      const allData = [
        { type: "Enrollments", data: enrollmentsReportData },
        { type: "EnrollmentsWithClaims", data: enrollmentClaimsReportData },
        { type: "Claims", data: claimsReportData },
        { type: "Orders", data: ordersReportData },
        { type: "CompletedOrders", data: completedOrdersReportData }
      ];
      isFetchingFinished && !isLoading && downloadFilesAsZip(allData);
    }
  }, [
    enrollmentsReportData,
    enrollmentClaimsReportData,
    claimsReportData,
    ordersReportData,
    completedOrdersReportData
  ]);

  async function fetchApplications() {
    const parsedFiltersObj = parseFilterValues(filters.timeRangeFilter);
    parsedFiltersObj.users = filters.users;
    parsedFiltersObj.facilities = filters.facilities;
    parsedFiltersObj.enrollmentDateTypeFilter = filters.enrollmentDateTypeFilter;
    parsedFiltersObj.selectedColumns = getSelectedColumns(ColumnsKeys.reports.enrollments);
    getEnrollmentsReportDataAct(parsedFiltersObj);
  }

  async function fetchClaims() {
    const parsedFiltersObj = parseFilterValues(filters.claimTimeRangeFilter);
    parsedFiltersObj.claimUsers = filters.claimUsers;
    parsedFiltersObj.claimFacilities = filters.claimFacilities;
    parsedFiltersObj.claimDateTypeFilter = filters.claimDateTypeFilter;
    parsedFiltersObj.selectedColumns = getSelectedColumns(ColumnsKeys.reports.claims);
    parsedFiltersObj.selectedLineItemColumns = getSelectedColumns(ColumnsKeys.reports.claim_line_items);
    getClaimsReportDataAct(parsedFiltersObj);
  }

  async function fetchEnrollmentClaims() {
    const parsedFiltersObj = parseFilterValues(filters.enrollmentClaimsTimeRangeFilter);
    parsedFiltersObj.enrollmentClaimsUsers = filters.enrollmentClaimsUsers;
    parsedFiltersObj.enrollmentClaimsFacilities = filters.enrollmentClaimsFacilities;
    parsedFiltersObj.selectedColumns = getSelectedColumns(ColumnsKeys.reports.enrollment_claims);
    getEnrollmentClaimsReportDataAct(parsedFiltersObj);
  }

  async function fetchOrders() {
    const parsedFiltersObj = parseFilterValues(filters.orderTimeRangeFilter);
    parsedFiltersObj.orderUsers = filters.orderUsers;
    parsedFiltersObj.orderFacilities = filters.orderFacilities;
    parsedFiltersObj.orderDateTypeFilter = filters.orderDateTypeFilter;
    parsedFiltersObj.selectedColumns = getSelectedColumns(ColumnsKeys.reports.orders);
    parsedFiltersObj.selectedLineItemColumns = getSelectedColumns(ColumnsKeys.reports.order_line_items);
    getOrdersReportDataAct(parsedFiltersObj);
  }

  async function fetchCompletedOrders() {
    const parsedFiltersObj = parseFilterValues(filters.completedOrderTimeRangeFilter);
    parsedFiltersObj.completedOrderUsers = filters.completedOrderUsers;
    parsedFiltersObj.completedOrderFacilities = filters.completedOrderFacilities;
    parsedFiltersObj.selectedColumns = getSelectedColumns(ColumnsKeys.reports.completed_orders);
    parsedFiltersObj.selectedLineItemColumns = getSelectedColumns(ColumnsKeys.reports.order_line_items);
    getCompletedOrdersReportDataAct(parsedFiltersObj);
  }

  const getReportFileName = (type, filters) => {
    const parsedFilters = parseFilterValues(filters);
    const dateRange =
      parsedFilters.fromDate && parsedFilters.toDate
        ? `${moment(parsedFilters.fromDate).format(REPORT_NAME_DATE_FORMAT)}-${moment(parsedFilters.toDate).format(
            REPORT_NAME_DATE_FORMAT
          )}_`
        : "";
    const dateDownloaded = moment().format(`${REPORT_NAME_DATE_FORMAT}`);
    return `${type}_${dateRange}Exported_${dateDownloaded}.xlsx`;
  };

  const downloadFile = (blob, filters, type) => {
    const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const url = URL.createObjectURL(new Blob([blob], { type: contentType }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", getReportFileName(type, filters));
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    SuccessMessage(`Report was exported successfully.`);
    clearData(type);
  };

  const clearData = (type) => {
    if (type === "Enrollments") setEnrollmentsReportDataAct();
    if (type === "EnrollmentsWithClaims") setEnrollmentClaimsReportDataAct();
    if (type === "Claims") setClaimsReportDataAct();
    if (type === "Orders") setOrdersReportDataAct();
    if (type === "CompletedOrders") setCompletedOrdersReportDataAct();
  };

  const downloadFilesAsZip = (allData) => {
    const dateDownloaded = moment().format(`${REPORT_NAME_DATE_FORMAT}`);
    const zip = new JSZip();
    allData.forEach((report) => {
      if (!report.data) return;
      let dateRangeFilter;
      if (report.type === "Enrollments") {
        dateRangeFilter = filters.timeRangeFilter;
      } else if (report.type === "EnrollmentsWithClaims") {
        dateRangeFilter = filters.enrollmentClaimsTimeRangeFilter;
      } else if (report.type === "Claims") {
        dateRangeFilter = filters.claimTimeRangeFilter;
      } else if (report.type === "Orders") {
        dateRangeFilter = filters.orderTimeRangeFilter;
      } else if (report.type === "CompletedOrders") {
        dateRangeFilter = filters.completedOrderTimeRangeFilter;
      }

      zip.file(getReportFileName(report.type, dateRangeFilter), report.data);
    });

    zip.generateAsync({ type: "blob" }).then((zipBlob) => {
      saveAs(zipBlob, `Multi_Reports_Exported_${dateDownloaded}.zip`);
    });
    SuccessMessage(`Report was exported successfully.`);

    setIsFetchingFinished(false);
    allData.forEach((report) => {
      clearData(report.type);
    });
  };
  return (
    <div id="export_report_btn_div">
      <StyledButton
        id="export_report_btn"
        isLoading={isLoading}
        onClick={handleReportExport}
        icon={isLoading ? buttonLoadingIcon : <ExportIcon />}
        disabled={!isExportEnabled}
      >
        {isLoading ? "" : t("data_types.export_report")}
      </StyledButton>
    </div>
  );
};

export default ExportButton;
